import React, { useState, useEffect, useContext } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import EventKit from "../../data/EventKit";
import InputTextComponent from "../../components/InputTextComponent";
import LabelComponent from "../../components/LabelComponent";
import AuthKit from "../../data/AuthKit";
import LayoutPrivate from "../../components/LayoutPrivate";
import firebaseKit from "../../data/firebase/firebaseKit";
import PageHead from "../../components/PageHead";
import eventStatus from "../../data/enums/eventStatus";
import { UserContext } from "../../contexts/UserContext";
import { ChatContext } from "../../contexts/ChatContext";
import { isMobileOrTablet } from "../../components/Views/MobileClassRenderer";
import { AudioAppContext } from "../../contexts/AudioContext";
import ProfilePictureSelector from "../../components/ProfilePictureSelector/ProfilePictureSelector";
import hasContent from "../../util/hasContent";

export default function CheckInPage(props) {
  const [userTitle, setUserTitle] = useState("");
  const [userCompany, setUserCompany] = useState("");
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");

  const { me, setMe } = useContext(UserContext);
  const { setAudioPlaybackAllowed } = useContext(AudioAppContext);

  const [eventData, setEventData] = useState(null);
  const [eventQuestionData, setEventQuestionData] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [token, setToken] = useState(null);
  const [firebaseToken, setFirebaseToken] = useState(null);
  const [firstRender, setFirstRender] = useState(true);

  const [formData, setFormData] = useState({});
  const [requiredData, setRequiredData] = useState("");
  const [valid, setValid] = useState(null);

  const [country, setCountry] = useState("");

  const { hasUnreadMessages } = useContext(ChatContext);

  const [errors, setErrors] = useState({});
  const router = useRouter();
  const { event, uuid, clientCountry } = router.query;

  const eventKit = new EventKit();
  const authKit = new AuthKit();

  useEffect(() => {
    authKit.deleteToken();
  }, []);

  const asyncLocalStorage = {
    setToken: async function (token) {
      await null;
      return authKit.setToken(token);
    },
  };

  useEffect(() => {
    if (eventQuestionData) {
      let newFormData = {};
      eventQuestionData.map((item) => {
        newFormData[item.slug] = undefined;
        return item.slug;
      });
      setFormData(newFormData);
    }
  }, [eventQuestionData]);

  useEffect(() => {
    const isValid = checkIsFormValid(formData);

    setValid(isValid);
  }, [formData]);

  function checkIsFormValid() {
    if (eventQuestionData && eventData) {
      return getAnswers().every(answerIsValid);
    }
    return false;
  }

  function getAnswers() {
    const {
      isTitleRequired,
      isCompanyRequired,
      isTitleVisible,
      isCompanyVisible,
    } = eventData;

    const questionData = [
      {
        answer: userFirstName,
        isRequired: true,
        slug: "firstname",
      },
      {
        answer: userLastName,
        isRequired: true,
        slug: "lastname",
      },
      {
        answer: userTitle,
        isRequired: isTitleRequired && isTitleVisible,
        slug: "title",
      },
      {
        answer: userCompany,
        isRequired: isCompanyRequired && isCompanyVisible,
        slug: "company",
      },
      ...eventQuestionData.map((question) => ({
        slug: question.slug,
        isRequired: question.isRequired,
        answer: formData[question.slug],
      })),
    ];

    return questionData;
  }

  function answerIsValid({ isRequired, answer }) {
    if (!isRequired) return true;

    if (!answer) return false;
    if (typeof answer === "string" && answer.trim().length < 1) {
      return false;
    }
    if (typeof answer == "object" && Object.keys(answer).length == 0)
      return false;

    return true;
  }

  useEffect(() => {
    if (uuid && event) {
      eventKit
        .checkInUser(event, uuid)
        .then((data) => {
          if (!data.error) {
            setToken(data.token);
            setFirebaseToken(data.firebaseToken);
            eventKit.getEventUserMe(event, data.token).then((data) => {
              setMe(data);
              setUserCompany(data.company);
              setUserTitle(data.title);
              setUserFirstName(data?.user?.firstName);
              setUserLastName(data?.user?.lastName);
              setFormData((prevState) => {
                if (!prevState) prevState = {};

                return {
                  ...formData,
                  ...data.answers,
                };
              });
            });
          } else {
            setErrors({ main: "Din länk verkar vara ogiltig" });
          }
        })
        .catch((errors) => {
          setErrors({ main: "Din länk verkar vara ogiltig" });
        });
    }
  }, [uuid, event]);

  useEffect(() => {
    if (event) {
      eventKit
        .getEventDetail(event)
        .then((data) => {
          setEventData(data);
        })
        .catch((errors) => setErrors({ main: "Din länk verkar vara ogiltig" }));

      eventKit.getEventQuestions(event).then((data) => {
        setEventQuestionData(data.results);
      });
    }
  }, [event]);
  

  useEffect(() => {
    if (eventData?.hasRussianEventUsers) {
      clientCountry && setCountry(clientCountry)
    };
  }, [eventData, router]);

  useEffect(() => {
    const handleEnter = (e) => {
      if (e.keyCode === 13) {
        e.preventDefault();
        handleOnSubmit();
      }
    };

    window.addEventListener("keydown", handleEnter);
    return () => {
      window.removeEventListener("keydown", handleEnter);
    };
  }, [me, userCompany, userTitle, userFirstName, userLastName, formData]);

  async function handleOnSubmit(e) {
    setFirstRender(false);
    e?.preventDefault();
    const payload = {
      id: me?.id,
      title: userTitle,
      company: userCompany,
      user: {
        firstName: userFirstName,
        lastName: userLastName,
      },
      answers: formData,
      checkIn: true
    };
    const formIsValid = checkIsFormValid();

    if (formIsValid) {
      const shouldUseProxy = country == "RU";
      eventKit
        .updateEventUserMe(event, payload, token, shouldUseProxy)
        .then(async (data) => {
          asyncLocalStorage.setToken(token).then(async () => {
            try {
              firebaseKit.auth.setToken(firebaseToken);
              await firebaseKit.auth.signInWithCustomToken(firebaseToken);
            } catch (error) {
              console.log("no Firebase", error)
            }
          });
          setAudioPlaybackAllowed(true);
          setMe(data);
          router.push("/[event]", `/${event}`);
        });
    } else {
      setValid(false);
      scrollToInvalidQuestion();
    }
  }

  function scrollToInvalidQuestion() {
    const answers = getAnswers();
    for (const answer in answers) {
      // console.log(answerIsValid(answers[answer]), answers[answer]);
      if (!answerIsValid(answers[answer])) {
        document.querySelector(`#${answers[answer].slug}`)?.scrollIntoView();
        setTimeout(() => {
          window.scrollBy(0, -50);
        }, 0);
        return;
      }
    }
  }

  function handleCheckBoxOnClick(e, question, alternative) {
    let newFormData = { ...formData };

    if (e.target.checked === false) {
      delete newFormData[question.slug][alternative.slug];
    } else {
      newFormData[question.slug] = {
        ...newFormData[question.slug],
        [alternative.slug]: e.target.checked,
      };
    }
    setFormData(newFormData);
  }

  function renderErrorMessage(message = "Please fill out this field") {
    return (
      <p className="alert-error error-text-message text-danger mb-3 mt-n3">
        {message}
      </p>
    );
  }

  function renderUserQuestion({ title, required, value, onChange, slug, disabled }) {
    return (
      <>
        <InputTextComponent
          title={`${title}`}
          placeholder={title}
          onChange={onChange}
          value={value}
          slug={slug}
          required={required}
          id={slug}
          disabled={disabled}
        />
        {!answerIsValid({ isRequired: required, answer: value }) &&
          !firstRender &&
          renderErrorMessage()}
      </>
    );
  }

  function renderQuestion(question) {
    if (question.kind === 0) {
      // Text
      return (
        <>
          <InputTextComponent
            key={question.id}
            id={question.slug}
            slug={question.slug}
            title={`${question.title}`}
            placeholder={question.title}
            onChange={(e) =>
              setFormData({ ...formData, [question.slug]: e.target.value })
            }
            value={formData[question.slug]}
            required={question.isRequired}
          />
          {!answerIsValid({
            isRequired: question.isRequired,
            answer: formData[question.slug],
          }) &&
            !firstRender &&
            renderErrorMessage("Please fill out this field")}
        </>
      );
    }

    if (question.kind === 1) {
      // Checkbox
      return (
        <div id={question.slug} key={question.id}>
          <LabelComponent
            title={question.title}
            required={question.isRequired}
          />

          {question.alternatives.map((alternative) => {
            return (
              <div
                key={alternative.id}
                className="custom-form-input-container"
                style={{ paddingBottom: "8px" }}
              >
                <label
                  htmlFor={alternative.slug}
                  className="form-label-checkbox"
                >
                  {alternative.title}
                  <input
                    type="checkbox"
                    id={alternative.slug}
                    name={alternative.slug}
                    value={alternative.slug}
                    checked={formData?.[question.slug]?.[alternative.slug]}
                    onClick={(e) =>
                      handleCheckBoxOnClick(e, question, alternative)
                    }
                    className="check-in-checkbox"
                  />
                  <span
                    class={`checkbox-checkmark ${
                      isMobileOrTablet()
                        ? "check-in-checkmark-mobile-tablet"
                        : ""
                    }`}
                  ></span>
                </label>
                <br />
              </div>
            );
          })}
          {!answerIsValid({
            isRequired: question.isRequired,
            answer: formData[question.slug],
          }) &&
            !firstRender &&
            renderErrorMessage("Please click on a checkbox")}
        </div>
      );
    }

    if (question.kind === 2) {
      // Radio button

      return (
        <div id={question.slug} key={question.id}>
          <LabelComponent
            title={question.title}
            required={question.isRequired}
          />
          {question.alternatives.map((alternative) => {
            return (
              <div
                key={alternative.id}
                className="custom-form-input-container"
                onChange={(e) =>
                  setFormData({ ...formData, [question.slug]: e.target.value })
                }
              >
                <label class="form-label">
                  {alternative.title}
                  <input
                    type="radio"
                    id={alternative.slug}
                    name={question.slug}
                    className="radio-input"
                    value={alternative.slug}
                    required={question.isRequired}
                    checked={formData?.[question.slug] === alternative.slug}
                  />
                  <span class="radio-checkmark"></span>
                </label>
                <br />
              </div>
            );
          })}
        </div>
      );
    }

    if (question.kind === 3) {
      // Select
      return (
        <div id={question.slug} key={question.id}>
          <LabelComponent
            title={question.title}
            required={question.isRequired}
          />
          <select
            className="select-custom-style"
            onChange={(e) =>
              setFormData({ ...formData, [question.slug]: e.target.value })
            }
          >
            <option value=""></option>
            {question.alternatives.map((alternative) => {
              return (
                <option
                  key={alternative.id}
                  value={alternative.slug}
                  selected={formData?.[question.slug] === alternative.slug}
                >
                  {alternative.title}
                </option>
              );
            })}
          </select>
          {!answerIsValid({
            isRequired: question.isRequired,
            answer: formData[question.slug],
          }) &&
            !firstRender &&
            renderErrorMessage("Please select an option")}
        </div>
      );
    }
  }

  return (
    <>
      {eventData && (
        <LayoutPrivate hasAnnouncement={false}>
          <PageHead
            eventTitle={eventData.title}
            suffix="Check in"
            hasUnreadMessages={hasUnreadMessages}
          />
          <>
            <div
              className="view-image-custom"
              style={{
                backgroundImage:
                  eventData.backgroundImageLarge &&
                  `url(${
                    eventData.backgroundImageLarge
                      ? eventData.backgroundImageLarge
                      : ""
                  })`,
                paddingBottom: "50px",
              }}
              data-bg={
                eventData.backgroundImageLarge
                  ? eventData.backgroundImageLarge
                  : "/assets/bgNotSet.png"
              }
            >
              {errors && errors.main && (
                <div className="text-center">
                  {hasContent(eventData.checkInErrorText) && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: eventData && eventData.checkInErrorText,
                      }}
                    ></div>
                  )}
                </div>
              )}
              {token && (
                <>
                  <img
                    src="/assets/badge-v2.png"
                    alt=""
                    className="company-badge"
                    width="98"
                  />
                  <form onSubmit={handleOnSubmit}>
                    <div className="sign-in check-in-wrapper">
                      {eventData.hasProfilePicture ? (
                        <>
                          <ProfilePictureSelector token={token} />
                        </>
                      ) : (
                        <>
                          {eventData.logotypeLink && (
                            <img
                              src={eventData.logotypeLink}
                              className="sing-in__logo"
                            />
                          )}
                        </>
                      )}
                      <div>
                        <div className="gform_heading"></div>
                        <div className="gform_body">
                          {/* {eventData.isFirstNameVisible && ( */}
                          {renderUserQuestion({
                            title: "First Name",
                            onChange: (e) => setUserFirstName(e.target.value),
                            value: userFirstName,
                            slug: "firstname",
                            required: true,
                            disabled: eventData.isFirstNameDisabled
                          })}
                          {/* )} */}

                          {/* {eventData.isLastNameVisible && ( */}
                          {renderUserQuestion({
                            title: "Last Name",
                            onChange: (e) => setUserLastName(e.target.value),
                            value: userLastName,
                            slug: "lastname",
                            required: true,
                            disabled: eventData.isLastNameDisabled
                          })}
                          {/* )} */}

                          {eventData.isTitleVisible && (
                            <>
                              {renderUserQuestion({
                                title: "Title",
                                onChange: (e) => setUserTitle(e.target.value),
                                value: userTitle,
                                slug: "title",
                                required: eventData.isTitleRequired,
                                disabled: eventData.isTitleDisabled
                              })}
                            </>
                          )}

                          {eventData.isCompanyVisible && (
                            <>
                              {renderUserQuestion({
                                title: "Company",
                                onChange: (e) => setUserCompany(e.target.value),
                                value: userCompany,
                                slug: "company",
                                required: eventData.isCompanyRequired,
                                disabled: eventData.isCompanyDisabled
                              })}
                            </>
                          )}

                          {eventQuestionData &&
                            eventQuestionData.map((question) => {
                              return renderQuestion(question);
                            })}

                          <button
                            className="gform_button check_in_button"
                            type="submit"
                          >
                            Check In
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </>
              )}
            </div>
          </>
        </LayoutPrivate>
      )}
    </>
  );
}
